/**
 * useMainData.ts
 */
import Category from '@shared/classes/category';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import { useMemo, useRef } from 'react';

interface MainDataProps {
  category: Category | undefined;
  channels: Rail | undefined;
  filter: Rail | undefined;
  rails: Rail[] | undefined;
  sliders: Slider[] | undefined;
}

/**
 * Base data object combining sliders, filter, channels and rails into one
 * ordered, position-indexed list — the shape `layout`/`selection` and
 * navigation are computed against. Also returns a ref mirror for callers
 * that need a stable, non-reactive read (e.g. inside callbacks).
 */
export default function useMainData({
  category,
  channels,
  filter,
  rails,
  sliders,
}: MainDataProps) {
  const data = useMemo(() => {
    return [
      sliders ? { id: 'sliders', data: sliders } : undefined,
      filter,
      channels,
      ...(rails || []),
    ]
      .filter((rail) => rail !== undefined)
      .filter((rail) => !!rail.data?.length)
      .map((rail, index) => ({
        id: rail?.id,
        data: rail?.data,
        position: index,
      }));
  }, [category, sliders, filter, channels, rails]);
  const dataRef = useRef(data);
  dataRef.current = data;

  return { data, dataRef };
}
